import {
  NgModule,
  ErrorHandler,
  APP_INITIALIZER,
  LOCALE_ID,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeNl from '@angular/common/locales/nl';
import { MessageService } from 'primeng/api';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import {
  LucideAngularModule,
  ChevronsLeft,
  ChevronsRight,
  ChevronRight,
  Home,
  Image,
  Users,
  DollarSign,
  Calculator,
  Book,
  HelpCircle,
  MoreVertical,
  User,
  Settings,
  LogOut,
  Percent,
  Construction,
  Shield,
  FlaskConical,
  HousePlus,
  Bike,
  Train,
  Plane,
  Bus,
  Car,
  Route,
  CircleCheck,
  Zap,
  Ship,
  MapPin,
  PlaneTakeoff,
  TicketsPlane,
  RulerDimensionLine,
  Timer,
  Map,
  Layers,
  FileEdit,
  CheckCircle,
  SearchX,
  Lock,
  TrendingUp,
  TrendingDown,
  CalendarPlus,
  CalendarMinus,
  Building,
  Building2,
  CreditCard,
  BarChart2,
  ChevronDown,
  List,
  Cloud,
  Leaf,
  BarChart3,
  Bot,
  Megaphone,
  // Help Center icons
  MessageCircle,
  GraduationCap,
  BookOpen,
  Mail,
  Brain,
  Video,
  Download,
  ExternalLink,
  Info,
  ListOrdered,
  ShieldCheck,
  PlusCircle,
  ListPlus,
  Calendar,
  Clock,
  Play,
  UserPlus,
  Trophy,
  Target,
  Check,
  Rocket,
  Presentation,
  Database,
  Eye,
  // How it works icons
  Upload,
  Edit,
  Compass,
  Utensils,
  Globe,
  Award,
  Heart,
  HandCoins,
  Lightbulb,
  Sparkles,
  ChartNoAxesCombined,
  TramFront,
  TriangleAlert,
  PlayCircle,
  X,
  RotateCcw,
  // Onboarding journey icons
  ArrowRight,
  ArrowDown,
  // Reduction tips apply dialog
  Copy,
} from 'lucide-angular';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { TranslocoRootModule } from './transloco-root.module';
import { MenubarModule } from 'primeng/menubar';
import { MegaMenuModule } from 'primeng/megamenu';
import { ImpactModule } from './impact/impact.module';
import { ToastModule } from 'primeng/toast';
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Lara from '@primeng/themes/lara';
import { UserModule } from './user/user.module';
import { ResourcesModule } from './resources/resources.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { credentialsInterceptor } from './interceptors/credentials.interceptor';
import { serverInterceptorProvider } from './interceptors/server-error.interceptor';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { TranslocoService } from '@jsverse/transloco';
import { definePreset } from '@primeng/themes';
import { ForumLeadCaptureModalComponent } from './shared/components/forum-lead-capture-modal/forum-lead-capture-modal.component';
import { BulkUploadProgressComponent } from './shared/components/bulk-upload-progress/bulk-upload-progress.component';

export function initLang(
  _traceService: Sentry.TraceService,
  translocoService: TranslocoService,
) {
  return () => {
    const savedLang = localStorage.getItem('selectedLanguage');
    if (savedLang) {
      translocoService.setActiveLang(savedLang);
    }
  };
}

// Register locales for proper number/date formatting
registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeNl);

const MyPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '#F5F8F6',
      100: '#DEE9E5',
      200: '#BCD3CA',
      300: '#93B5A8',
      400: '#6C9587',
      500: '#527A6D',
      600: '#3D5D53',
      700: '#364F47',
      800: '#2E413C',
      900: '#293834',
      950: '#141F1D',
    },
    success: {
      50: '#F3FAF3',
      100: '#E3F5E4',
      200: '#C8EACB',
      300: '#9CD9A2',
      400: '#69BF71',
      500: '#429E4B',
      600: '#33863C',
      700: '#2B6A32',
      800: '#27542C',
      900: '#214626',
      950: '#0E2511',
    },
    warn: {
      50: '#FCF8EA',
      100: '#F7EECA',
      200: '#F1DA97',
      300: '#E9BF5B',
      400: '#E0A329',
      500: '#D18F21',
      600: '#B46E14',
      700: '#904F18',
      800: '#78401B',
      900: '#67361C',
      950: '#3B1B0D',
    },
    danger: {
      50: '#FEF3EE',
      100: '#FDE4D7',
      200: '#F9C6AF',
      300: '#F59E7C',
      400: '#F06C47',
      500: '#EB431E',
      600: '#DD2F19',
      700: '#B72117',
      800: '#921C1A',
      900: '#761A18',
      950: '#3F0B0C',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#FFFFFF',
          50: '#F6F7F6',
          100: '#ECEEEC',
          200: '#E3E7E3',
          300: '#CFD2CF',
          400: '#B1B5B1',
          500: '#8C928C',
          600: '#7D857D',
          700: '#686E68',
          800: '#575D57',
          900: '#4A514A',
          950: '#2A2D2A',
        },
      },
    },
  },
  components: {
    togglebutton: {
      checked: {
        background: '{primary.100}',
        color: '{primary.800}',
        border: {
          color: '{primary.400}',
        },
      },
    },
    button: {
      colorScheme: {
        light: {
          outlined: {
            primary: {
              hoverBackground: '{primary.100}',
            },
          },
        },
      },
    },
  },
});

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    UserModule,
    AuthModule,
    ResourcesModule,
    ImpactModule,
    TranslocoRootModule,
    MenubarModule,
    MegaMenuModule,
    ForumLeadCaptureModalComponent,
    BulkUploadProgressComponent,
    ToastModule,
    LucideAngularModule.pick({
      ChevronsLeft,
      ChevronsRight,
      ChevronRight,
      Home,
      Image,
      Users,
      DollarSign,
      Calculator,
      Book,
      HelpCircle,
      MoreVertical,
      User,
      Settings,
      LogOut,
      Percent,
      Construction,
      Shield,
      X,
      FlaskConical,
      HousePlus,
      Bike,
      Plane,
      Train,
      Bus,
      Car,
      Route,
      CircleCheck,
      Zap,
      Ship,
      MapPin,
      PlaneTakeoff,
      TicketsPlane,
      RulerDimensionLine,
      Timer,
      Map,
      Layers,
      FileEdit,
      CheckCircle,
      SearchX,
      TrendingUp,
      TrendingDown,
      CalendarPlus,
      CalendarMinus,
      BarChart2,
      ChevronDown,
      List,
      Cloud,
      Leaf,
      BarChart3,
      Megaphone,
      Building,
      Building2,
      CreditCard,
      Bot,
      Lock,
      // Help Center icons
      MessageCircle,
      GraduationCap,
      BookOpen,
      Mail,
      Brain,
      Video,
      Download,
      ExternalLink,
      Info,
      ListOrdered,
      ShieldCheck,
      PlusCircle,
      ListPlus,
      Calendar,
      Clock,
      Play,
      UserPlus,
      Trophy,
      Target,
      Check,
      Rocket,
      Presentation,
      Database,
      Eye,
      // How it works icons
      Upload,
      Edit,
      Compass,
      Utensils,
      Globe,
      Award,
      Heart,
      HandCoins,
      Lightbulb,
      Sparkles,
      ChartNoAxesCombined,
      TramFront,
      TriangleAlert,
      PlayCircle,
      RotateCcw,
      // Onboarding journey icons
      ArrowRight,
      ArrowDown,
      // Reduction tips apply dialog
      Copy,
    }),
  ],
  providers: [
    MessageService,
    credentialsInterceptor,
    serverInterceptorProvider,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.p-dark',
          // cssLayer: {
          //   name: 'primeng',
          //   order: 'tailwind-base, primeng, tailwind-utilities',
          // },
        },
      },
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initLang,
      deps: [Sentry.TraceService, TranslocoService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      deps: [TranslocoService],
      useFactory: (transloco: TranslocoService) => {
        const saved = localStorage.getItem('selectedLanguage') || '';
        const lang = transloco.getActiveLang?.() || saved || 'en';
        const map: Record<string, string> = {
          fr: 'fr',
          en: 'en-US',
          de: 'de',
          nl: 'nl',
        };
        return map[lang] || 'en-US';
      },
    },
  ],
})
export class AppModule {}
